/** Pure JSON body parsing for Requests/Responses -- the utils core of the JSON boundary. The always-LOGGING flavors
 * stay in the tools response-json leaf (they bind `logError` off the deps registry); this leaf owns the
 * callback-injected parse path so tools, DOs, workers, flows and BROWSER hooks compose it without a cross-tool edge.
 * Disconnect results are tested via the property-shape check in `is-disconnected-result`.
 *
 * THE PURE RESPONSE READERS LIVE HERE, not beside the logging flavors, because none binds a logger or does I/O of its
 * own: `parseOkResponseJsonOrThrow` is `coreParseJson` plus an `ok` guard plus a `BaseError`, and
 * `safeParseResponseJson` / `checkAndParseResponse` (moved from the tools leaf 2026-08-25 on that leaf's own contract)
 * add only a const-reachable message and a status read. Their consumers include `components/hooks/*`,
 * `components/messaging` and `spa/chats`, and a non-flow file importing a `tools/` module is
 * exactly what `QUAL-NO-NONFLOW-TOOL-IMPORTS-001` forbids. */

import { BaseError } from '@federa/const/base-error';
import { ERROR_CODES } from '@federa/const/error-codes';
import { PARSE_JSON_DISCONNECT_KEY, type ParseJsonDisconnectResult } from '@federa/const/parse-json-disconnect-sentinel';
import { errorMessageOrUnknown, requestFailedWithStatus } from '@federa/const/error-messages';
import { errorMessageOrString } from '@federa/const/error-text';
import { ERROR } from '@federa/const/error-values';
import { httpErrorContextDetails } from '@federa/const/http-error-context';
import { extractErrorText } from '@federa/const/http-response-check';
import { HTTP_STATUS } from '@federa/const/http-status';
import { isPlainObject } from '@federa/const/record-guards';
import { nonEmptyStringOrNull } from '@federa/const/string-guards';

/** Failure result from coreParseJson with optional disconnect discrimination. */
interface CoreParseJsonFailure {
	ok: false;
	error: unknown;
	disconnected: boolean;
}

/** Core JSON parser. All public parsing functions delegate here. */
export async function coreParseJson(source: Request | Response): Promise<{ ok: true; data: unknown } | CoreParseJsonFailure> {
	try {
		// ANNOTATED `unknown`, not inferred: `json()` is declared `Promise<any>` on both `Request` and `Response`, so the
		// inferred binding was `any` and eslint's `no-unsafe-assignment` blocked this line. `unknown` is what the result
		// type already promises and what every caller narrows from, so the annotation removes the `any` at its entry
		// point rather than letting it travel one hop and be re-widened at the return.
		const data: unknown = await source.json();
		return { ok: true, data };
	} catch (err) {
		const disconnected = errorMessageOrString(err).toLowerCase().includes('client disconnected');
		return { ok: false, error: err, disconnected };
	}
}

interface ParseJsonWithLoggingOptions {
	onError?: (err: unknown) => void;
}

/** Null result from parseJsonWithLogging, tagged as a client disconnect. The KEY is
 *  `PARSE_JSON_DISCONNECT_KEY` (`const/parse-json-disconnect-sentinel.ts`), not a literal: the sole RECOGNIZER of this
 *  tag is `isDisconnectedResult` (`utils/http/is-disconnected-result.ts`), which may not value-import this leaf, so a
 *  const home is the only thing that can hold the producer and the recognizer to one spelling. */
type ParseJsonNullResult = ParseJsonDisconnectResult;

const DISCONNECT_SENTINEL: ParseJsonNullResult = { [PARSE_JSON_DISCONNECT_KEY]: true };

/** Parses JSON from a Request or Response, returning the parsed value or `null` on failure. LOGGING: `options.onError` is the injected failure-reporting seam (no logger-hub import here) -- callers own the failure record, and `QUAL-DO-JSON-PARSE-001` steers callers to supply it; `parseJsonWithErrorLogging` in the tools response-json leaf is the always-logging variant. Contract: the parsed JSON on success; on a non-disconnect parse failure `null` (invoking `onError` first if supplied); on a client disconnect `null` when no `onError` is supplied, else the disconnect sentinel after invoking it. THAT CONTRACT LIVES HERE BECAUSE THE TYPE CANNOT HOLD IT: parsed JSON is genuinely `unknown`, and `unknown` already admits `null`, so the `Promise<unknown | null>` this returned until 2026-09-03 was the identical type spelled to look like a discriminated one. Callers narrow on the value (`=== null`, or `isDisconnectedResult`), never on the signature. */
export async function parseJsonWithLogging(source: Request | Response, options: ParseJsonWithLoggingOptions = {}): Promise<unknown> {
	const result = await coreParseJson(source);
	if (result.ok) return result.data;
	if (result.disconnected) {
		if (options.onError) {
			options.onError(new Error('request.body_disconnected'));
			return DISCONNECT_SENTINEL;
		}
		return null;
	}
	options.onError?.(result.error);
	return null;
}

/** Typed wrapper that casts the result to Record or null. */
export async function parseJsonAsRecord(
	source: Request | Response,
	options: ParseJsonWithLoggingOptions = {},
): Promise<Record<string, unknown> | null> {
	return (await parseJsonWithLogging(source, options)) as Record<string, unknown> | null;
}


/**
 * The DENIAL DISCRIMINANT a non-OK body may carry: a plain-object body whose `error` member is a non-empty string.
 *
 * The shape is NOT invented here -- it is the one `extractErrorBody` (`components/lib/api/fetch-helpers.ts`) already
 * lifts off a non-OK response before `handleErrorResponse` puts it on `AdminAPIError.message`, so a gate denial reads
 * the same whether the transport or this parser is the surface that observes it. Deliberately NOT gated on
 * `success === false`: a denial that carries its wording under `error` while omitting the success tag still named the
 * requirement, and the tag adds nothing on a response that is already non-OK.
 *
 * Answers null for every other body, which is what makes the caller FAIL CLOSED on the status rather than let a
 * fabricated discriminant through.
 */
function denialMessageOrNull(body: unknown): string | null {
	if (!isPlainObject(body)) return null;
	return nonEmptyStringOrNull(body.error);
}

/**
 * Parse JSON from a Response, THROWING `BaseError` on both failure arms: `HTTP_JSON_PARSE_FAILED` when an OK body is
 * present but unparseable, and `HTTP_REQUEST_FAILED` for a non-OK response.
 *
 * THE NON-OK ARM SURFACES THE SERVER'S OWN WORDING. It formerly answered `null`, which discarded the denial body
 * entirely -- a 403 carrying `{ success: false, error: 'insufficient_privileges' }` reached every caller as an
 * indistinguishable absence, and each one re-minted a generic "failed to load" over the sentence that named the actual
 * requirement. The status now rides `BaseError.status` (and `details.status`) as DATA, so a caller discriminating a
 * refusal class reads a typed member instead of parsing prose. A body carrying no discriminant -- an HTML error page,
 * an empty body, a JSON scalar -- falls back to `requestFailedWithStatus`, the SSOT sentence for exactly the "HTTP
 * client could not read a server-supplied error" case; nothing is invented from an unreadable body.
 *
 * NOT `parseJsonSafe`: that name is the canonical SYNCHRONOUS string parser in `const/json-parse.ts` and shares nothing
 * with this but a spelling -- this one is async, takes a `Response`, and THROWS where the other returns a fallback.
 */
export async function parseOkResponseJsonOrThrow(response: Response, context: string = ''): Promise<unknown> {
	const suffix = context ? ` for ${context}` : '';
	const result = await coreParseJson(response);
	if (!response.ok) {
		throw new BaseError({
			code: ERROR_CODES.HTTP_REQUEST_FAILED,
			message: (result.ok ? denialMessageOrNull(result.data) : null) ?? `${requestFailedWithStatus(response.status)}${suffix}`,
			status: response.status,
			details: context ? { status: response.status, context } : { status: response.status },
		});
	}
	if (result.ok) return result.data;
	throw new BaseError({
		code: ERROR_CODES.HTTP_JSON_PARSE_FAILED,
		message: `Failed to parse JSON${suffix}: ${errorMessageOrUnknown(result.error)}`,
		cause: result.error,
		details: httpErrorContextDetails(context),
	});
}

/**
 * Never-throwing Response JSON parse: `{ ok: true, data }` or `{ ok: false, error }` carrying a prefixed sentence.
 *
 * MOVED HERE FROM THE TOOLS RESPONSE-JSON LEAF (2026-08-25) on that leaf's OWN stated contract, the same ground
 * {@link parseOkResponseJsonOrThrow} moved on: this binds no logger and builds no `Response`, so the
 * logging/response-building home was a misfile. Its consumers include `components/`, `mfe/` and `spa/` modules, and a
 * non-flow file importing a `tools/` module is exactly what `QUAL-NO-NONFLOW-TOOL-IMPORTS-001` forbids.
 *
 * THE MESSAGE IS NOT A RESPELLING OF `buildErrorResponse`. That builder (`utils/errors/error-core.ts`) is a utils
 * sibling this leaf may not value-import (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, no allowlist), and only its `error`
 * member was ever read here -- which is `prefix` + `getErrorMessage(err)` and nothing else. `getErrorMessage`'s
 * no-default body is {@link errorMessageOrUnknown}, const-homed for exactly this reason, so the sentence composes
 * from the same SSOT, byte-for-byte -- and the throwing reader above words the same cause the same way.
 */
export async function safeParseResponseJson(response: Response): Promise<{ ok: true; data: unknown } | { ok: false; error: string }> {
	const result = await coreParseJson(response);
	if (result.ok) return result;
	return {
		ok: false,
		error: `${ERROR.FAILED_TO_PARSE_RESPONSE_JSON}: ${errorMessageOrUnknown(result.error)}`,
	};
}

/**
 * Status-then-parse Response reader: a non-OK response answers the server's own body text under `error` beside its
 * `status`; an OK-but-unparseable body answers the {@link safeParseResponseJson} sentence under a 500. Never throws.
 *
 * HOMED WITH ITS PARSE ARM, same ground: no logger binding, no `Response` construction. Both collaborators are const
 * leaves -- `extractErrorText` (`const/http-response-check.ts`, homed there for this identical utils-floor reason) and
 * `HTTP_STATUS` -- so this home takes no utils-sibling value edge.
 */
export async function checkAndParseResponse(
	response: Response,
): Promise<
	{ ok: false; error: string; status: number; data?: undefined } | { ok: true; data: unknown; error?: undefined; status?: undefined }
> {
	if (!response.ok) {
		return {
			ok: false,
			error: await extractErrorText(response),
			status: response.status,
		};
	}
	const parseResult = await safeParseResponseJson(response);
	if (!parseResult.ok)
		return {
			ok: false,
			error: parseResult.error,
			status: HTTP_STATUS.INTERNAL_SERVER_ERROR,
		};
	return { ok: true, data: parseResult.data };
}
